import React from 'react'
import clsx from 'clsx'
import { Lock } from 'iconsax-react'
import { useInView } from 'react-hook-inview'
import { useTheme } from '@mui/material'
import { useDrop } from 'react-dnd'

import OverflowText from '@SonarCX/components/atoms/OverflowText'
import Tooltip from '@SonarCX/components/atoms/Tooltip'
import TaskCards from '../TaskCards'
import LoadingTaskCard from '../LoadingTaskCard'
// import NoTasks from '../NoTasks'
import Button from '@SonarCX/components/atoms/Button2'

import { toTitleCase } from '@SonarCX/utilities'
import { useTaskCardState } from '@SonarCX/providers/TaskCardStateProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useTaskPageState } from '@SonarCX/providers/TaskPageStateProvider'
import { useTaskSWRMutateCache } from '@SonarCX/providers/TaskSWRMutateCache'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { useTaskFilters } from '@SonarCX/providers/TaskFiltersProvider'
import useFavorites from '@SonarCX/hooks/useFavorites'
import useInfiniteFetch from '@SonarCX/hooks/useInfiniteFetch'
import useStyles from './styles'

export const TASK_CARD_ITEM_TYPE = 'task-card'

type ColumnProps = {
  value: { id: string; name: string | null }
  isPrivate?: boolean
  isNone?: boolean
}

const Column: React.FC<ColumnProps> = ({ value, isPrivate, isNone }) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const [inViewRef, inView] = useInView({ unobserveOnEnter: true })

  const { filterID, parsedExtra, extraFilters } = useTaskCardState()
  const { formatTask, updateTask } = useTaskAPI()
  const { lastViewedColumn, setLastViewedColumn } = useTaskPageState()
  const { updateCardChange } = useTaskSWRMutateCache()
  const { onlyFavorites } = useTaskFilters()
  const { favoriteTasks } = useFavorites()

  const handleDrop = React.useCallback(
    async (item: any) => {
      try {
        const { task } = item
        const newTask = { ...task, [filterID]: value.id }
        await updateTask(newTask.id, newTask)
        updateCardChange(task, newTask)
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [filterID, value.id, updateTask, updateCardChange, dispatch]
  )

  const [{ isOver, isCurrent }, dropRef] = useDrop({
    accept: TASK_CARD_ITEM_TYPE,
    drop: (item: any) => handleDrop(item),
    canDrop: (item: any) => item.task[filterID] !== value.id,
    collect: monitor => ({
      isOver: !!monitor.isOver(),
      isCurrent: monitor.getItem()?.task?.[filterID] === value.id
    })
  })

  const type = React.useMemo(() => {
    const splitted = filterID.split('_')
    return splitted.length > 0 ? splitted[0] : ''
  }, [filterID])

  const isHidden = React.useMemo(() => {
    // Only show null values for categories (api doesn't support others)
    if (isNone) {
      return filterID !== 'category_id'
    }

    const pluralType = `${filterID}s`
    // Not hidden when not specified or all selected
    if (!(pluralType in parsedExtra)) {
      return false
    }

    // Hidden when not explicitly included
    return !(parsedExtra[pluralType] as string[]).includes(value.id)
  }, [isNone, filterID, parsedExtra, value])

  const { flatData, isLoading, canLoadMore, loadMore } = useInfiniteFetch(
    () => {
      // Disable fetch for hidden or off-screen columns
      if (isHidden || !inView) {
        return null
      }

      // Don't allow fetch with no parameters since some are required
      if (!extraFilters) {
        return null
      }

      // Construct url
      let url = `${extraFilters}`
      const id = value.id === null ? 'false' : value.id
      if (id) {
        url += `&${filterID}s[]=${id}`
      }

      if (url.length === 0) {
        return null
      }

      return `allTasks:${url}`
    }
  )

  const tasks: LocalTask[] = React.useMemo(() => {
    if (onlyFavorites)
      return flatData
        .filter(flat => favoriteTasks.some(f => f.item_id === flat.id))
        .flatMap(task => formatTask(task))

    return flatData.flatMap(task => formatTask(task))
  }, [favoriteTasks, flatData, formatTask, onlyFavorites])

  const name = React.useMemo(() => {
    if (!isNone) {
      return value.name
    }

    if (!type) {
      return null
    }

    return `No ${toTitleCase(type)}`
  }, [isNone, value, type])

  const containerID = React.useMemo(() => `${value.id}-${value.name}`, [value])
  const columnRef = React.useRef<HTMLDivElement>()

  const classes = useStyles()

  // Scroll to column of last viewed task
  React.useEffect(() => {
    if (containerID === lastViewedColumn) {
      if (columnRef.current) {
        setLastViewedColumn(null)
        columnRef.current.scrollIntoView({
          // @ts-ignore
          behavior: 'instant',
          block: 'end',
          inline: 'center'
        })
      }
    }
  }, [containerID, lastViewedColumn, setLastViewedColumn])

  if (!name || isHidden) {
    return null
  }

  return (
    <div
      ref={el => {
        inViewRef(el)
        columnRef.current = el
        dropRef(el)
      }}
      id={containerID}
      className={clsx(classes.root, isOver && !isCurrent && classes.over)}
    >
      <div className={classes.title}>
        <OverflowText
          className={clsx(
            classes.name,
            isPrivate
              ? classes.namePrivate
              : isNone
              ? classes.nameNone
              : classes.namePublic
          )}
        >
          {name}
        </OverflowText>

        {isPrivate && (
          <Tooltip
            title={`This ${type ? `${type} ` : ''}is private`}
            placement='right'
            arrow
          >
            <div>
              <Lock variant='Bold' size={theme.size.base} color='gray' />
            </div>
          </Tooltip>
        )}
      </div>

      {inView ? (
        <TaskCards tasks={tasks} containerID={containerID}>
          {/* {isEmpty && <NoTasks />} */}

          {isLoading && <LoadingTaskCard />}

          {canLoadMore && (
            <Button onClick={() => loadMore()} className={classes.loadButton}>
              Load More
            </Button>
          )}
        </TaskCards>
      ) : (
        <div />
      )}
    </div>
  )
}

export default Column
